import React from 'react';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { CommonPopupBodyFooterLayout } from '../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../global/sub-window/common/common-close-sub-window-buttons';
import { APPLY_TEXT, CANCEL_TEXT, PASSPORT_PREVIEW_TEXT } from '../../../../../constants/common-labels.constants';
import ConfigurePassportDisplayVM from './configure-passport-display.viewmodel';
import { FormFieldInput } from '../../../../global/form/form-field-input/form-field-input';
import { ConfigurePassportDisplayFieldKey, CONNECT_DETAILS_ENABLED_LABEL_TEXT, FORM_CONFIG, PASSPORT_CONFIGURATION_OTHER_CONNECTIONS } from './configure-passport-display.config';
import { FormField } from '../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../global/rich-text-editor';
import { EnablementStatus } from '../../../../../constants/common.constants';
import Text from '../../../../global/text/text';
import { FormFieldProps } from '../../../../../infrastructure/form-viewmodel-controller';
import { ConnectionType } from '../../../../../api/connections.dao';
import { ConfigurePassportPreviewPopover } from './configure-passport-preview/configure-passport-preview-popover';
import { PassportDisplayHeader } from './passport-display-header';
import './configure-passport-display.scss';

interface IConfigurePassportDisplayPresentation {
    vm: ConfigurePassportDisplayVM;
    intl: IntlShape;
}

const ConfigurePassportDisplayPresentation = injectIntl(observer(({ vm, intl }: IConfigurePassportDisplayPresentation): JSX.Element => {
    const {
        passportLabel,
        passportDescription,
        connectButtonLabel,
        connectionDetailsEnabled,
        showDialInInfo,
        hideDialInInfo,
    } = vm.getFormFields(ConfigurePassportDisplayFieldKey);

    const getFieldPrefix = (value: boolean): string => {
        return value ? EnablementStatus.DISPLAYED : EnablementStatus.HIDDEN;
    };

    const toggleField = (field: FormFieldProps): void => {
        field.setValue(!field.value);
    };

    // [OEP-30761] - temporary comment configuration toggle handler
    // const toggleShowHideButton = (field: FormFieldProps): void => {
    //     toggleField(field);
    //     if (!!field.value) {
    //         vm.setShowHideDialToDefaultValues();
    //     }
    // };

    const isAudioBridgeConnection = vm.connectionType === ConnectionType.AudioBridge;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const isOthersConnection = PASSPORT_CONFIGURATION_OTHER_CONNECTIONS.includes(vm.connectionType as ConnectionType);

    if (vm.isLoading) return (
        <CommonPopupBodyFooterLayout
            className='oe-form'
            body={
                <div>
                    <FormattedMessage
                        id='oe.common.loading'
                        defaultMessage='Loading{suffix}'
                        description='Loading placeholder'
                        values={{
                            suffix: '...',
                        }}
                    />
                </div>
            }
        />
    );

    return (
        <CommonPopupBodyFooterLayout
            body={<>
                <PassportDisplayHeader
                    connectionLabel={vm.connectionLabel}
                    onClickPreview={() => vm.setPassportPreviewOpen(true)}
                />
                <form className='oe-form'>
                    <div className='oe-form__body'>
                        <FormFieldInput.Text
                            field={passportLabel}
                            labelDescriptor={FORM_CONFIG[ConfigurePassportDisplayFieldKey.PASSPORT_LABEL].labelDescriptor}
                            withError
                        />

                        <FormField label={<FormattedMessage {...FORM_CONFIG[ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION].labelDescriptor} />}>
                            <RichTextEditor
                                className='m-top-0px m-bottom-15px'
                                value={passportDescription.value as string}
                                onChange={mobx.action((event, editor) => {
                                    passportDescription.setValue(editor.getData());
                                })}
                                onBlur={passportDescription.setTouched}
                                validationError={
                                    !passportDescription.valid && passportDescription.touched &&
                                    <FormattedMessage
                                        values={{
                                            fieldName: intl.formatMessage(FORM_CONFIG[ConfigurePassportDisplayFieldKey.PASSPORT_DESCRIPTION].labelDescriptor)
                                        }}
                                        {...passportDescription.validationMessage}
                                    />
                                }
                            />
                        </FormField>

                        {isOthersConnection && <>
                            <FormFieldInput.Text
                                field={connectButtonLabel}
                                labelDescriptor={FORM_CONFIG[ConfigurePassportDisplayFieldKey.CONNECT_BUTTON_LABEL].labelDescriptor}
                                withError
                            />
                            <FormFieldInput.Toggle
                                isSelected={connectionDetailsEnabled.value as boolean}
                                toggle={toggleField.bind(null, connectionDetailsEnabled)}
                                text={<Text
                                    {...CONNECT_DETAILS_ENABLED_LABEL_TEXT}
                                    values={{
                                        prefix: getFieldPrefix(connectionDetailsEnabled.value as boolean)
                                    }}
                                />}
                            />
                        </>
                        }
                        {isAudioBridgeConnection && <>
                            {/* [OEP-30761] - temporary hide configuration toggle
                             <FormFieldInput.Toggle
                                isSelected={showHideButtonEnabled.value as boolean}
                                toggle={toggleShowHideButton.bind(null, showHideButtonEnabled)}
                                text={<Text
                                    {...SHOW_HIDE_BUTTON_ENABLED_LABEL_TEXT}
                                    values={{
                                        prefix: getFieldPrefix(showHideButtonEnabled.value as boolean)
                                    }}
                                />}
                            />*/}
                            <FormFieldInput.Text
                                field={showDialInInfo}
                                labelDescriptor={FORM_CONFIG[ConfigurePassportDisplayFieldKey.SHOW_DIAL_IN_INFO].labelDescriptor}
                                withError
                            />
                            <FormFieldInput.Text
                                field={hideDialInInfo}
                                labelDescriptor={FORM_CONFIG[ConfigurePassportDisplayFieldKey.HIDE_DIAL_IN_INFO].labelDescriptor}
                                withError
                            />
                        </>
                        }
                    </div>
                </form>
                <ConfigurePassportPreviewPopover
                    titlePopover={`${intl.formatMessage(PASSPORT_PREVIEW_TEXT)}: ${vm.connectionLabel}`}
                    popoverIsOpen={vm.isPassportPreviewModalOpen}
                    closePopover={() => vm.setPassportPreviewOpen(false)}
                    vm={vm}
                />
            </>
            }
            footer={<>
                {vm.isSaving && <FormattedMessage
                    id='oe.customFields.dataIsBeingSaved'
                    defaultMessage='Preloader message text'
                    description='Data is being saved, please wait{suffix}'
                    values={{
                        suffix: '...'
                    }}
                />
                }
                <div className='buttons-block buttons-block--from-right'>
                    <DefaultCloseSubWindowButton
                        text={intl.formatMessage(APPLY_TEXT)}
                        onClick={vm.submitForm}
                        modifier={['fat', 'confirm-safe']}
                        disabled={!vm.formVM?.isVmValid() || vm.isSaving}
                    />
                    <DefaultCloseSubWindowButton
                        text={intl.formatMessage(CANCEL_TEXT)}
                    />
                </div>
            </>}
        />
    );
}));

export { ConfigurePassportDisplayPresentation };
